/**
 * CreatorClientSwitcher
 *
 * Top-right workspace switcher for the creator portal. A single creator
 * email may have `creator`-role rows across multiple clients; each client
 * the user has signed into on this device is persisted as a
 * `touchpoint_portal_token_<clientId>` entry in localStorage.
 *
 * The device-session list is unioned with the server's view of every
 * client this session's email can reach (`/api/client-portal/me/clients`),
 * so workspaces the creator could open but hasn't signed into yet still
 * appear. Selecting one that lacks a stored token exchanges for a fresh
 * client-scoped session (`/api/client-portal/me/switch`) before navigating.
 *
 * Rules:
 * - If only one workspace is available, the current client name is rendered
 *   as static text (no dropdown). Falls back to `Cliente <id-prefix>` when
 *   no cached name is present.
 * - With multiple workspaces, a DropdownMenu lists every accessible client
 *   and navigates to `/creator/<clientId>` on selection.
 */

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  DropdownMenu,
  type DropdownMenuEntry,
} from '@/components/ui/DropdownMenu';
import { ChevronDownIcon, ClientIcon, CheckIcon } from '@/components/icons';
import { BRAND_GRADIENT_TRACK } from '@/components/ui/brandGradient';
import { listPortalSessions } from '@/core/auth/listPortalSessions';
import { hasPortalAuth, setPortalAuth } from '@/core/auth/storage';
import { useHttpClient } from '@/core/http/HttpClientContext';
import { portalMeService } from '../services/portalMeService';
import { mergeWorkspaces, type CreatorWorkspace } from '../creatorWorkspaces';

interface CreatorClientSwitcherProps {
  /** Currently active creator workspace (the `:clientId` route param). */
  activeClientId: string;
  /** Active client display name, when the auth layer has cached it. */
  activeClientName: string | null;
}

/**
 * Produce a user-facing label for a workspace. Prefers the cached client
 * name and falls back to a shortened UUID so the dropdown never renders
 * an empty row.
 */
function labelForSession(workspace: CreatorWorkspace): string {
  if (workspace.clientName && workspace.clientName.trim().length > 0) {
    return workspace.clientName;
  }
  return `Cliente ${workspace.clientId.slice(0, 8)}`;
}

export const CreatorClientSwitcher: React.FC<CreatorClientSwitcherProps> = ({
  activeClientId,
  activeClientName,
}) => {
  const navigate = useNavigate();
  const http = useHttpClient();

  // Server-known workspaces for this session's email. Sessions minted
  // from a per-brief share link have no email identity — the endpoint
  // returns [] and the device-session list below carries the day.
  const { data: serverWorkspaces } = useQuery({
    queryKey: ['portal', 'me', 'clients', activeClientId],
    queryFn: () => portalMeService.getMyClients(http),
    staleTime: 5 * 60 * 1000,
  });

  const workspaces = useMemo(
    () => mergeWorkspaces(serverWorkspaces ?? [], listPortalSessions('creator')),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serverWorkspaces, activeClientId]
  );

  const activeLabel = useMemo(() => {
    const matching = workspaces.find(
      (workspace) => workspace.clientId === activeClientId
    );
    if (matching) return labelForSession(matching);
    if (activeClientName && activeClientName.trim().length > 0) {
      return activeClientName;
    }
    return `Cliente ${activeClientId.slice(0, 8)}`;
  }, [workspaces, activeClientId, activeClientName]);

  const handleSelect = async (workspace: CreatorWorkspace) => {
    if (workspace.clientId === activeClientId) return;
    if (!hasPortalAuth(workspace.clientId)) {
      const session = await portalMeService.switchClient(
        http,
        workspace.clientId
      );
      setPortalAuth(workspace.clientId, {
        token: session.accessToken,
        refreshToken: session.refreshToken,
        role: 'creator',
        clientName: session.clientName ?? undefined,
      });
    }
    navigate(`/creator/${workspace.clientId}`);
  };

  // Single workspace -> static label. Avoids a meaningless dropdown.
  // Same gradient pill as the admin ClientSelector, minus the chevron.
  if (workspaces.length <= 1) {
    return (
      <div
        className={`flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-900 ${BRAND_GRADIENT_TRACK} rounded-lg truncate`}
      >
        <ClientIcon className="w-4 h-4 text-gray-500 shrink-0" />
        <span className="truncate">{activeLabel}</span>
      </div>
    );
  }

  const items: DropdownMenuEntry[] = workspaces.map((workspace) => ({
    id: workspace.clientId,
    label: labelForSession(workspace),
    icon:
      workspace.clientId === activeClientId ? (
        <CheckIcon className="w-4 h-4 text-blue-600" />
      ) : (
        <span className="w-4 h-4 inline-block" />
      ),
    onClick: () => {
      void handleSelect(workspace).catch((err) => {
        console.error('[CreatorClientSwitcher] switch failed:', err);
      });
    },
  }));

  const trigger = (
    // Same gradient-track pill as the admin ClientSelector trigger.
    <button
      type="button"
      className={`flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-900 ${BRAND_GRADIENT_TRACK} hover:bg-white/40 rounded-lg transition-colors`}
    >
      <ClientIcon className="w-4 h-4 text-gray-500 shrink-0" />
      <span className="truncate max-w-[12rem]">{activeLabel}</span>
      <ChevronDownIcon className="w-4 h-4 text-gray-500 shrink-0" />
    </button>
  );

  return (
    <DropdownMenu
      items={items}
      trigger={trigger}
      align="right"
      width="w-56"
    />
  );
};

export default CreatorClientSwitcher;
